import type { DishListing, ListingStatus } from '../types/listing';
import { zonedWallClockToInstant } from './timezone';

// Statuses where the listing can no longer be edited in place. Opening one in
// the publish form drops the user into a DUPLICATE: the old listing's data is
// pre-filled, but every save points at a brand-new listing.
export const RELIST_SOURCE_STATUSES: ReadonlySet<ListingStatus> = new Set<ListingStatus>([
  'expired',
  'canceled',
  'sold_out',
]);

/**
 * True when `listing` can only be duplicated, never edited.
 *
 * Two ways a listing gets here:
 *   1. Status is canceled / expired / sold_out (explicit retirement).
 *   2. `timeWindow.end` is in the past. The expiry worker retires listings on
 *      a tick, so there's a gap where a window has closed but the status is
 *      still active/scheduled — we check the time ourselves rather than trust
 *      the label. Drafts are always editable.
 *
 * This is the single source of truth for the Duplicate-vs-Edit affordance
 * (ListingDetailsPage) and for the publish form's relist mode
 * (PublishListingPage), so the button a chef taps always matches what the
 * form then does. The backend rejects `expired → scheduled` outright
 * (`status transition from 'expired' is not allowed`), so any surface that
 * offers "Edit" on one of these is offering a dead end.
 */
export function isRelistSource(listing: Pick<DishListing, 'status' | 'timeWindow'> | null | undefined): boolean {
  if (!listing) return false;
  if (listing.status === 'draft') return false;
  if (RELIST_SOURCE_STATUSES.has(listing.status)) return true;

  const end = listing.timeWindow?.end;
  if (!end) return false;
  return zonedWallClockToInstant(end, listing.timeWindow!.timezone).getTime() < Date.now();
}
